import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.min.css'
import SwiperCore, { Navigation, Pagination } from 'swiper'
import ResponsiveImage from 'ui/responsive_image'
import { useTranslation } from 'react-i18next'

// Activar módulos Swiper
SwiperCore.use([Navigation, Pagination])

// ==== ESTILOS ====

const GridContainer = styled.div`
  display: grid;
  gap: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 10px;
  }
`

const Item = styled.div`
  position: relative;
  max-height: ${({ large }) => (large ? '500px' : '235px')};
  grid-column: ${({ col }) => col};
  grid-row: ${({ row }) => row};
`

const ProductImage = styled(ResponsiveImage)`
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transition: 0.2s;

  &:hover {
    /* Rendimiento: Hasta bajar resolución de fotos */
    /* transform: scale(1.01); */
    cursor: pointer;
    box-shadow: var(--bs-box-shadow-lg) !important;
  }
`

const Overlay = styled.div.attrs({className: 'p-2'})`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;

  &:hover {
    cursor: pointer;
    background: rgba(0, 0, 0, 0.5);

    button {
      background: var(--bs-primary);
      color: var(--bs-white);
    }
  }
`

const OverlayButton = styled.button.attrs({
  className: 'px-1 px-md-3 py-1 py-md-2 rounded-5 border-0 fw-semibold'
})`
  background: rgba(255, 255, 255, 0.9);
  color: #000;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
`

const SliderOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;

  /* FLECHAS */
  .swiper-button-next,
  .swiper-button-prev {
    color: var(--bs-primary) !important; /* color flechas */
  }

  .swiper-button-next::after,
  .swiper-button-prev::after {
    font-size: 35px !important; /* tamaño flechas */
    font-weight: bold;
  }

  /* PAGINADOR */
  .swiper-pagination-bullet {
    background: rgba(255, 255, 255, 0.4) !important;
  }

  .swiper-pagination-bullet-active {
    background: var(--bs-primary) !important;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 25px;
  right: 35px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 999999;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
`

// ==== COMPONENTE ====

const ProductMasonry = ({ title, featured_image, images = [] }) => {
  const [showSlider, setShowSlider] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const allImages = [featured_image, ...images].filter(Boolean);
  const masonryImages = allImages.slice(0, 5)
  const count = allImages.length;
  const {i18n} = useTranslation()

  // 🔹 Cerrar slider con tecla ESC
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setShowSlider(false);
      }
    };

    if (showSlider) {
      document.addEventListener('keydown', handleEsc);
    } else {
      document.removeEventListener('keydown', handleEsc);
    }

    // Limpieza al desmontar
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showSlider]);

  // === Layout de grilla ===
  let gridStyle = {};
  let positions = [];

  switch (count) {
    case 1:
      gridStyle = { gridTemplateColumns: '1fr' };
      positions = [{ col: '1 / -1', row: '1 / -1', large: true }];
      break;
    case 2:
      gridStyle = { gridTemplateColumns: '2fr 1fr' };
      positions = [
        { col: '1 / 2', row: '1 / 2', large: true },
        { col: '2 / 3', row: '1 / 2', large: true },
      ];
      break;
    case 3:
      gridStyle = {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'repeat(2, 1fr)',
      };
      positions = [
        { col: '1 / 2', row: '1 / 3', large: true },
        { col: '2 / 3', row: '1 / 2' },
        { col: '2 / 3', row: '2 / 3' },
      ];
      break;
    case 4:
      gridStyle = {
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
      };
      positions = [
        { col: '1 / 2', row: '1 / 3', large: true },
        { col: '2 / 3', row: '1 / 3', large: true },
        { col: '3 / 4', row: '1 / 2' },
        { col: '3 / 4', row: '2 / 3' },
      ];
      break;
    case 5:
    default:
      gridStyle = {
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
      };
      positions = [
        { col: '1 / 2', row: '1 / 3', large: true },
        { col: '2 / 3', row: '1 / 2' },
        { col: '3 / 4', row: '1 / 2' },
        { col: '2 / 3', row: '2 / 3' },
        { col: '3 / 4', row: '2 / 3' },
      ];
      break;
  }

  return (
    <>
      {/* === GRID === */}
      <GridContainer style={gridStyle}>
        {masonryImages.map((src, i) => (
          <Item key={i} {...positions[i]}>
            <ProductImage
              src={src}
              alt={`${title} - ${i + 1}`}
              onClick={() => {
                setActiveIndex(i);
                setShowSlider(true);
              }}
            />
            
            {i === allImages.length - 1 && count >= 2 && (
              <Overlay
                onClick={() => {
                  setActiveIndex(i);
                  setShowSlider(true);
                }}
              >
                <OverlayButton>
                  <i className="fa fa-image me-2" /> {i18n.t('format:actions.see_photos')}
                </OverlayButton>
              </Overlay>
            )}
          </Item>
        ))}
      </GridContainer>

      {/* === SLIDER FULLSCREEN === */}
      {showSlider && (
        <SliderOverlay>
          <CloseButton onClick={() => setShowSlider(false)}>×</CloseButton>
          <Swiper
            navigation
            pagination={{ clickable: true }}
            loop={true}
            initialSlide={activeIndex}
            style={{ width: '90%', height: '90%' }}
          >
            {allImages.map((src, i) => (
              <SwiperSlide key={i}>
                <img
                  src={src['1200w']}
                  alt={`Foto ${i + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderOverlay>
      )}
    </>
  )
}

export default ProductMasonry
